import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next'; 
import { getInsuranceVerificationDetails } from 'src/redux/services/commonServices';
import NoData from '../NoData';
import { InsuranceVerificationHeader } from './InsuranceVerificationHeader';
import { InsurancePlanDetails } from './InsurancePlanDetails';
import { InsuranceNetworkStatus } from './InsuranceNetworkStatus';
import { InsuranceDeductible } from './InsuranceDeductible';
import { InsuranceCopay } from './InsuranceCopay';
import { InsuranceCoInsurance } from './InsuranceCoInsurance';
import { InsuranceOutOfPocketExpense } from './InsuranceOutOfPocketExpense';
import { InsuranceNotes } from './InsuranceNotes';
import {
  InsuranceEligibilityResponse,
  MeetingInformation,
  NetworkStatusEnums,
} from 'src/latest/src/types';
import { LoadingIndicator } from '../LoadingIndicator';
import './InsuranceVerificationDetails.scss';
import { InsuranceSessionLimit } from './InsuranceSessionLimit';
import { bookingStatus, getUserDataById } from 'src/api/user';
import { InsuranceOopSessionFee } from './InsuranceOopSessionFee';
import { getTherapistClinicianAvailabilityData } from 'src/redux/services/questionnaireServices';

type Props = {
  userId: string;
  question: any;
  setUploading: (uploading: boolean) => void;
};

export const InsuranceVerificationDetails: React.FC<Props> = ({
  userId,
  question,
  setUploading,
}) => {
  const { t } = useTranslation('insuranceVerification');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [insuranceVerificationData, setInsuranceVerificationData] =
    useState<InsuranceEligibilityResponse>();
  const [meetingInfo, setMeetingInfo] = useState<MeetingInformation>();
  const [outOfPocketFee, setOutOfPocketFee] = useState<string | number>();

  const fetchInsuranceVerificationDetails = async (userId: string) => {
    try {
      setIsLoading(true);
      setUploading(true);

      const response = await getInsuranceVerificationDetails(userId);
      const meetingInfo = await bookingStatus(userId, 'large');
      
      const userDetails = await getUserDataById(userId,false, 'large');

      if (meetingInfo) {
        setMeetingInfo(meetingInfo);
        const clinicianAvailablityData = await getTherapistClinicianAvailabilityData(
          meetingInfo.therapist_id
        );

          if (clinicianAvailablityData) {
            setOutOfPocketFee(clinicianAvailablityData.session_fee);
          }

        
      }

      if (response) {
        
        setInsuranceVerificationData({
        ...response,
        client_name: userDetails?.full_name || response.client_name,
      });
      }
    } catch (error) {
      console.error('Error fetching insurance verification details:', error);
    } finally {
      setIsLoading(false);
      setUploading(false);
    }
  };

  useEffect(() => {
    fetchInsuranceVerificationDetails(userId);
  }, [userId]);

  const renderInsuranceDetails = () => {
    if (!insuranceVerificationData) {
      return (
        <NoData
          message={t('messages.noDataAvailable')}
          className="mt-5"
        />
      );
    }

    return (
      <>
        <InsurancePlanDetails
          insuranceVerificationData={insuranceVerificationData}
          patientName={insuranceVerificationData?.client_name}
          className="mt-1"
        />
      <InsuranceNetworkStatus
            payerName={insuranceVerificationData.payer_name}
            networkStatus={insuranceVerificationData.network_status}
            serviceCoveredInNetwork={insuranceVerificationData.service_covered_in_net}
            meetingInfo={meetingInfo}
            className="mt-5"
          />

       { (outOfPocketFee && ((insuranceVerificationData?.service_covered_in_net && insuranceVerificationData?.service_covered_in_net === 'NO' ) || insuranceVerificationData?.network_status === 'OUT_OF_NETWORK')) && <>
      
         <InsuranceOopSessionFee
              outOfPocketFee={outOfPocketFee}
              className="mt-5"
            />

        </>}
        
       {((insuranceVerificationData?.service_covered_in_net && insuranceVerificationData?.service_covered_in_net === 'YES') && (insuranceVerificationData?.network_status === NetworkStatusEnums.IN_NETWORK || insuranceVerificationData?.network_status === NetworkStatusEnums.IN_REVIEW)) && <>
          {(insuranceVerificationData.estimated_copay && insuranceVerificationData?.estimated_copay?.trim()?.length > 0 )&& <>
           <InsuranceCopay
              estimatedCopay={insuranceVerificationData.estimated_copay}
              className="mt-5"
            />
            </>}

            {(Object.entries(insuranceVerificationData?.session_limit).length > 0 && (insuranceVerificationData?.session_limit?.visit_remaining_in_net?.trim()?.length > 0 || insuranceVerificationData?.session_limit?.visit_limitation_in_net?.trim()?.length > 0)) && (
              <InsuranceSessionLimit
                annualLimit={insuranceVerificationData.session_limit.visit_limitation_in_net}
                remaining={insuranceVerificationData.session_limit.visit_remaining_in_net}
                className="mt-5"
              />
            )}

            {(Object.entries(insuranceVerificationData?.deductibles).length > 0 && (insuranceVerificationData?.deductibles?.amount_applied_to_deductible?.trim()?.length > 0 || insuranceVerificationData.deductibles?.deductible_remaining?.trim()?.length > 0 || insuranceVerificationData?.deductibles?.plan_individual_deductible?.trim()?.length > 0)) && (
              <InsuranceDeductible
                deductibles={insuranceVerificationData.deductibles}
                className="mt-5"
              />
            )}

            {(insuranceVerificationData?.estimated_coinsurance && insuranceVerificationData?.estimated_coinsurance?.trim()?.length > 0) && (
              <InsuranceCoInsurance
                estimatedCoinsurance={
                  insuranceVerificationData.estimated_coinsurance
                }
                className="mt-5"
              />
            )}

            {(Object.entries(insuranceVerificationData?.out_of_pocket_max).length > 0 && (insuranceVerificationData?.out_of_pocket_max?.amount_applied_to_oop_max?.trim()?.length > 0 || insuranceVerificationData?.out_of_pocket_max.plan_oop_max?.trim()?.length > 0)) && (
              <InsuranceOutOfPocketExpense
                outOfPocketExpense={insuranceVerificationData.out_of_pocket_max}
                className="mt-5"
              />
            )}
          </>}

        <InsuranceNotes className="mt-5" />
      </>
    );
  };

  return (
    <div className="insurance-verification-details-container d-flex flex-column align-items-center">
      {question?.description && (
        <InsuranceVerificationHeader description={question.description} />
      )}
      <div className="content-section">
        {isLoading ? (
          <div style={{ height: '200px' }}>
            <LoadingIndicator loading={isLoading} />
          </div>
        ) : (
          renderInsuranceDetails()
        )}
        <br />
      </div>
    </div>
  );
};
